import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { getSessions, signOutAllSessions } from "@/lib/auth";
import { chipToast } from "@/components/ui/chip-toast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SettingRow } from "./profile-form";
import { sessionIcon, sessionSubtitle } from "./session-display";

export function SignOutDevicesDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useLingui();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ["auth", "sessions"],
    queryFn: getSessions,
    enabled: open,
  });
  const otherSessions = sessions.filter((s) => !s.isCurrent);

  const signOutMutation = useMutation({
    mutationFn: signOutAllSessions,
    onSuccess: () => {
      chipToast({ message: t`Signed out of all devices.` });
      queryClient.clear();
      navigate({ to: "/login", search: { redirect: "/dashboard" } });
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : t`Failed to sign out`,
      );
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-175">
        <DialogHeader>
          <DialogTitle><Trans>Sign out from other devices</Trans></DialogTitle>
          <DialogDescription>
            <Trans>
              Review where you&rsquo;re signed in and end those sessions.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="flex items-start gap-3 rounded-lg bg-(--color-warning-bg) p-3">
            <TriangleAlert className="mt-0.5 size-5 shrink-0 text-(--color-warning-text)" />
            <div>
              <p className="text-sm font-medium text-(--color-warning-text)">
                <Trans>Please note</Trans>
              </p>
              <p className="text-sm text-(--color-text-secondary)">
                <Trans>
                  Sessions can only be ended all at once, so you&rsquo;ll also
                  be signed out on this device and need to sign in again.
                </Trans>
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium text-(--color-text-primary)">
              <Trans>Other active sessions</Trans>
            </p>
            {isLoading ? (
              <div className="flex items-center gap-2 py-4 text-sm text-(--color-text-secondary)">
                <Loader2 className="size-4 animate-spin" />
                <Trans>Loading sessions…</Trans>
              </div>
            ) : otherSessions.length === 0 ? (
              <p className="rounded-lg border border-(--color-border) p-4 text-sm text-(--color-text-secondary)">
                <Trans>No other active sessions.</Trans>
              </p>
            ) : (
              otherSessions.map((session) => (
                <SettingRow
                  key={session.id}
                  icon={sessionIcon(session.device)}
                  title={session.device}
                  description={sessionSubtitle(session, i18n)}
                />
              ))
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={signOutMutation.isPending}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => signOutMutation.mutate()}
            disabled={signOutMutation.isPending}
          >
            {signOutMutation.isPending ? t`Signing out…` : t`Sign out all devices`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
